import { normalize } from 'normalizr';
import { camelizeKeys } from 'humps';
import tokenService from 'services/tokenService';
import { saveAs } from 'file-saver';
import { toFormData, getFullUrl, getCommonHeaders, createQueryParams, getFileName, getGenericFileName, decamelizeKeys, composeError } from './utils';
import { ensureValidToken, forceRefreshToken } from '../../services/authService';

const fetchAndDownloadBlob = (url, options) => fetch(url, options)
  .then(async response => {
    const contentDisposition = response.headers.get('Content-Disposition');

    const fileName = contentDisposition ? getFileName(contentDisposition) : getGenericFileName();

    if (!response.ok && response.json) {
      const jsonResponse = camelizeKeys(await response.json());

      return { ok: response.ok, status: response.status, error: composeError(jsonResponse) };
    }

    return response.blob().then(blob => {
      saveAs(blob, fileName);

      return { ok: response.ok };
    });
  });

const fetchAndParseResponse = (url, options, schema) => fetch(url, options)
  .then(async response => {
    if (response.status === 204) {
      return { ok: response.ok, error: null, response: {} };
    }

    const jsonResponse = camelizeKeys(await response.json());
    const { data, meta, links, exception } = jsonResponse;

    if (!response.ok || exception) {
      return { ok: response.ok, status: response.status, error: composeError(jsonResponse), response: undefined };
    }

    const { result, entities } = schema ? normalize(data, schema) : { result: data };

    return {
      ok: true,
      error: null,
      response: schema || meta || links
        ? { data: result, entities, links, meta } // Pagination & schema results
        : result, // Everything else
    };
  })
  .catch(error => ({ ok: false, error, response: undefined }));

const send = async (endpoint, method, data, { schema = null, formData = false, blob = false, anonymous = false, fetchOptions = {}, headers = {} }, token) => {
  const commonHeaders = anonymous ? getCommonHeaders() : getCommonHeaders(token);

  const options = {
    ...fetchOptions,
    method,
    headers: {
      ...(!formData && !blob && { 'Content-Type': 'application/json' }),
      ...commonHeaders,
      ...headers,
    },
  };

  // Compile the url
  let fullUrl = getFullUrl(endpoint);
  if (method === 'GET' && data && Object.keys(data).length !== 0) {
    fullUrl += `?${createQueryParams(decamelizeKeys(data))}`;
  }

  if (formData) {
    options.body = toFormData(data);
  } else if (method !== 'GET' && data) {
    options.body = JSON.stringify(decamelizeKeys(data));
  }

  return blob
    ? fetchAndDownloadBlob(fullUrl, options)
    : fetchAndParseResponse(fullUrl, options, schema);
};

const request = async (endpoint, method = 'GET', data = {}, options = {}) => {
  const { auth = true, anonymous = false } = options;

  if (auth && !anonymous) {
    await ensureValidToken();
  }

  const token = anonymous ? null : await tokenService.getAccessToken();
  const result = await send(endpoint, method, data, options, token);

  if (result.status !== 401 || !auth || anonymous) {
    return result;
  }

  const refreshed = await forceRefreshToken(token);

  if (!refreshed.ok) {
    return result;
  }

  return send(endpoint, method, data, options, await tokenService.getAccessToken());
};

export default request;
